import { Capacitor } from '@capacitor/core'

export interface StudioFaceLandmark {
  x: number
  y: number
  z: number
}

export type StudioFaceLandmarks = StudioFaceLandmark[][]

let worker: Worker | null = null
let nextRequestId = 0
const pendingRequests = new Map<number, { resolve: (faces: StudioFaceLandmarks) => void; reject: (error: Error) => void }>()

interface VisionSdk {
  FaceLandmarker: {
    createFromOptions(fileset: { wasmLoaderPath: string; wasmBinaryPath: string }, options: unknown): Promise<{
      detect(source: HTMLImageElement | HTMLCanvasElement | ImageBitmap): {
        faceLandmarks?: Array<Array<{ x: number; y: number; z: number }>>
      }
    }>
  }
}

let visionScriptPromise: Promise<VisionSdk> | null = null
const ensureVisionScript = (): Promise<VisionSdk> => {
  const existing = (window as unknown as { Vision?: VisionSdk }).Vision
  if (existing) return Promise.resolve(existing)
  if (visionScriptPromise) return visionScriptPromise
  visionScriptPromise = new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is undefined'))
      return
    }
    const script = document.createElement('script')
    script.src = `${import.meta.env.BASE_URL}vendor/mediapipe/vision_bundle.js`
    script.async = true
    script.onload = () => {
      const Vision = (window as unknown as { Vision?: VisionSdk }).Vision
      if (Vision) resolve(Vision)
      else reject(new Error('MediaPipe Vision script yüklendi ancak Vision objesi bulunamadı.'))
    }
    script.onerror = (error) => {
      visionScriptPromise = null
      reject(error instanceof Error ? error : new Error('MediaPipe Vision scripti yüklenemedi.'))
    }
    document.head.appendChild(script)
  })
  return visionScriptPromise
}

let mainLandmarkerPromise: Promise<unknown> | null = null
const getMainLandmarker = async () => {
  if (mainLandmarkerPromise) return mainLandmarkerPromise
  const Vision = await ensureVisionScript()
  const fileset = {
    wasmLoaderPath: `${import.meta.env.BASE_URL}vendor/mediapipe/vision_wasm_internal.js`,
    wasmBinaryPath: `${import.meta.env.BASE_URL}vendor/mediapipe/vision_wasm_internal.wasm`,
  }
  const options = {
    runningMode: 'IMAGE',
    numFaces: 4,
    minFaceDetectionConfidence: 0.30,
    minFacePresenceConfidence: 0.30,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: false,
    baseOptions: {
      modelAssetPath: `${import.meta.env.BASE_URL}models/face_landmarker.task`,
      delegate: 'CPU',
    },
  }
  mainLandmarkerPromise = Vision.FaceLandmarker.createFromOptions(fileset, options).catch((error) => {
    mainLandmarkerPromise = null
    throw error
  })
  return mainLandmarkerPromise
}

const detectFacesMainThread = async (source: ImageBitmap | HTMLImageElement): Promise<StudioFaceLandmarks> => {
  const landmarker = await getMainLandmarker() as {
    detect(source: HTMLImageElement | HTMLCanvasElement | ImageBitmap): {
      faceLandmarks?: Array<Array<{ x: number; y: number; z: number }>>
    }
  }
  const result = landmarker.detect(source)
  if (!result || !result.faceLandmarks) return []
  return result.faceLandmarks.map((face: Array<{ x: number; y: number; z: number }>) =>
    face.map(({ x, y, z }: { x: number; y: number; z: number }) => ({ x, y, z }))
  )
}

const getWorker = () => {
  if (worker) return worker
  worker = new Worker(new URL(`${import.meta.env.BASE_URL}workers/studio-face-worker.js`, window.location.origin), { type: 'classic' })
  worker.onmessage = (event: MessageEvent<{ id: number; faces?: StudioFaceLandmarks; error?: string }>) => {
    const pending = pendingRequests.get(event.data.id)
    if (!pending) return
    pendingRequests.delete(event.data.id)
    if (event.data.error) pending.reject(new Error(event.data.error))
    else pending.resolve(event.data.faces ?? [])
  }
  worker.onerror = (event) => {
    const error = new Error(event.message || 'Yüz analizi başlatılamadı.')
    pendingRequests.forEach(({ reject }) => reject(error))
    pendingRequests.clear()
    worker?.terminate()
    worker = null
  }
  return worker
}

export const detectStudioFaces = async (source: ImageBitmap | HTMLImageElement): Promise<StudioFaceLandmarks> => {
  if (source instanceof ImageBitmap && (source.width === 0 || source.height === 0)) {
    console.warn('[Voxnela Retouch] ImageBitmap is detached, cannot detect faces')
    return []
  }

  // In native Capacitor (iOS), WKWebView does not route worker requests to WKURLSchemeHandler.
  // Run MediaPipe directly in the main thread where capacitor:// scheme works properly.
  if (Capacitor.isNativePlatform()) {
    try {
      return await detectFacesMainThread(source)
    } catch (error) {
      console.warn('[Voxnela Retouch] Native face detection error:', error)
      return []
    }
  }

  // On web: try Web Worker first, fallback to main thread if worker fails
  try {
    // ALWAYS create a fresh clone ImageBitmap so source is NEVER transferred or detached in main thread!
    const bitmap = await createImageBitmap(source)
    const id = ++nextRequestId
    return await new Promise<StudioFaceLandmarks>((resolve, reject) => {
      pendingRequests.set(id, { resolve, reject })
      try {
        getWorker().postMessage({ id, bitmap }, [bitmap])
      } catch (error) {
        pendingRequests.delete(id)
        bitmap.close()
        reject(error instanceof Error ? error : new Error('Yüz analizi başlatılamadı.'))
      }
    })
  } catch (workerErr) {
    console.warn('[Voxnela Retouch] Worker detection failed, falling back to main thread:', workerErr)
    try {
      return await detectFacesMainThread(source)
    } catch (mainErr) {
      console.warn('[Voxnela Retouch] Main thread detection also failed:', mainErr)
      return []
    }
  }
}
